import React, { useEffect, useRef, useState } from "react";
import cls from "classnames";
import { TooltipTrigger, TooltipTriggerProps } from "../core/Tooltip/TooltipTrigger";
import styles from "./CopyableText.module.scss";
import { isElementOverflowing } from "../../utils/dom";
import { text } from "../../utils/i18n";

type Props = {
  value: string;
  previewValueInTooltip?: boolean;
  props?: Omit<Partial<TooltipTriggerProps>, 'ref'>;
}

/**
 * Copies some text to the clipboard. Checks for permissions.
 */
export async function copyTextToClipboard(text: string) {
  return await _copyTextToClipboard(text, false);
}

async function _copyTextToClipboard(text: string, isRetry: boolean) {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    console.error("Error copying text to clipboard", error);

    if (!isRetry) {
      try {
        const result = await navigator.permissions.query({ name: "clipboard-write" as any });
        if (result.state == "granted" || result.state == "prompt") {
          return await _copyTextToClipboard(text, true);
        } else {
          throw new Error("Permission denied");
        }
      } catch (error) {
        console.error("Error requesting clipboard write permission", error);
        throw error;
      }
    }

    throw error;
  }
}

export type CopyToClipboardStatus = 'progress' | 'copied' | 'error' | null;

/**
 * Number of milliseconds to show a confirmation that text has been copied to clipboard.
 */
export const copiedStateDuration = 1250;

/**
 * Renders text which can be copied on click.
 */
export const CopyableText: React.FC<Props> = ({ children, value, previewValueInTooltip = false, props }) => {
  const [copyToClipboardStatus, setCopyToClipboardStatus] = useState<CopyToClipboardStatus>(null);
  const [isOverflow, setOverflow] = useState(false);
  const mouseDownAt = useRef(Date.now());

  useEffect(() => {
    if (copyToClipboardStatus === 'error' || copyToClipboardStatus === 'copied') {
      const timeoutId = setTimeout(() => setCopyToClipboardStatus(null), copiedStateDuration);
      return () => { clearTimeout(timeoutId); }
    }
  }, [copyToClipboardStatus]);

  async function copyToClipboard() {
    if (copyToClipboardStatus === 'progress') {
      return;
    }

    setCopyToClipboardStatus('progress');

    try {
      await copyTextToClipboard(value);
      setCopyToClipboardStatus('copied');
    } catch (error) {
      setCopyToClipboardStatus('error');
    }
  }

  let tooltipText: string = text`Klicka för att kopiera`;
  if (previewValueInTooltip || isOverflow) {
    tooltipText = value + '\n\n' + text`Klicka för att kopiera.`;
  }

  if (copyToClipboardStatus === 'progress') {
    tooltipText = text`Kopierar` + '...';

  } else if (copyToClipboardStatus === 'copied') {
    tooltipText = text`Kopierat!`;

  } else if (copyToClipboardStatus === 'error') {
    tooltipText = text`Kunde inte kopiera text`;

  }

  return (
    <TooltipTrigger
      ref={element => {
        if (!element) {
          return;
        }

        const _isOverflow = isElementOverflowing(element);
        if (_isOverflow !== isOverflow) {
          setOverflow(isOverflow);
        }
      }}
      interactive="hoverable"
      role="button"
      delay={false}
      tooltip={tooltipText}
      onMouseDown={() => mouseDownAt.current = Date.now()}
      onClick={() => {
        if (Date.now() - mouseDownAt.current < 400) {
          //  If the mouse has been held down for a time then the user likely trying to highlight some text,
          //  would be annoying to copy the text then.
          copyToClipboard();
        }
      }}
      {...props}
      className={cls([copyToClipboardStatus ? styles.copied : null, props?.className])}>
      {children}
    </TooltipTrigger>
  )
}